import type { ReactElement } from 'react';
import { HoverCard as HoverCardPrimitive } from 'radix-ui';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { Clock, MapPin, User, Video, FileText, StickyNote, Phone, Cake } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { DaySlot } from '@vagusx/clients';
import { APPOINTMENT_STATUS_LABELS, APPOINTMENT_STATUS_COLORS } from './hooks/use-schedule';

interface SlotHoverCardProps {
  slot: DaySlot;
  /** Practice/location timezone the times are rendered in (matches the grid). */
  displayTz: string;
  providerName: string;
  locationName: string | null;
  /**
   * Controlled open state. Owned by the grid so only one card is ever open: hover
   * opens this slot's card (and closes any other), and the search prev/next
   * controls open the active match by setting it here. `onOpenChange` carries
   * Radix's hover/focus intent back up to the grid.
   */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The scrollable grid container. Used as the card's collision boundary so it
   * stays inside the appointments panel instead of floating up over the date-nav
   * header when its trigger slot scrolls toward the top edge.
   */
  boundary?: HTMLElement | null;
  /** The SlotCell button — wrapped as the hover trigger (must forward ref + props). */
  children: ReactElement;
}

/** "9:00 AM – 9:30 AM" in the grid's display timezone. */
function timeRange(slot: DaySlot, tz: string): string {
  const start = toZonedTime(slot.startTime, tz);
  const end = toZonedTime(slot.endTime, tz);
  return `${format(start, 'h:mm a')} – ${format(end, 'h:mm a')}`;
}

/**
 * "Dec 20, 1985 · 40 yrs" from an ISO `yyyy-mm-dd` DOB. Parsed from parts (not
 * `new Date(iso)`) so the date-only value isn't shifted a day by UTC-midnight
 * parsing in negative-offset timezones. Returns null if the string is malformed.
 */
function formatDob(dob: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dob);
  if (!match) return null;
  const [, y, m, d] = match;
  const year = Number(y);
  const month = Number(m);
  const day = Number(d);
  const birth = new Date(year, month - 1, day);
  if (Number.isNaN(birth.getTime())) return null;

  const now = new Date();
  let age = now.getFullYear() - year;
  if (now.getMonth() + 1 < month || (now.getMonth() + 1 === month && now.getDate() < day)) {
    age--;
  }
  return `${format(birth, 'MMM d, yyyy')} · ${age} yrs`;
}

function Row({
  icon: Icon,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex items-start gap-2 text-xs text-foreground/90">
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <span className="min-w-0 break-words">{children}</span>
    </div>
  );
}

/**
 * Rich hover preview for a calendar slot. Open slots show type + time + the
 * provider/location they'd book against; booked slots add the patient name,
 * status, telehealth flag, reason, and notes. Hover/focus driven so it never
 * blocks the click-to-book / click-for-details interaction on the cell.
 *
 * The trigger is mounted up front so it lives in the browser's hover chain and
 * fires reliable enter/leave events — Radix opens after `openDelay` and closes
 * after `closeDelay`, giving normal one-card-at-a-time behavior. The richer
 * content (timezone formatting, etc.) is only built when the card is actually
 * open, since Radix gates Content behind `Presence`. (An earlier version mounted
 * the HoverCard lazily by swapping in the trigger on first hover; that remounted
 * the button under a stationary cursor, so it never received `pointerenter`/
 * `pointerleave` and `defaultOpen` cards got stuck open across the grid.)
 */
export function SlotHoverCard({
  slot,
  displayTz,
  providerName,
  locationName,
  open,
  onOpenChange,
  boundary,
  children,
}: SlotHoverCardProps): React.ReactElement {
  const isOpen = slot.status === 'open';
  const appt = slot.appointment;
  const patient = appt?.patient;
  const statusLabel = appt ? (APPOINTMENT_STATUS_LABELS[appt.status] ?? 'Booked') : null;
  const statusColor = appt ? APPOINTMENT_STATUS_COLORS[appt.status] : undefined;
  const dobLabel = patient?.dob ? formatDob(patient.dob) : null;

  return (
    <HoverCardPrimitive.Root
      open={open}
      onOpenChange={onOpenChange}
      openDelay={150}
      closeDelay={80}
    >
      <HoverCardPrimitive.Trigger asChild>{children}</HoverCardPrimitive.Trigger>
      <HoverCardPrimitive.Portal>
        <HoverCardPrimitive.Content
          side="right"
          align="start"
          sideOffset={6}
          // Keep the card within the scrollable grid (not just the viewport) so it
          // never floats up over the date-nav header above the panel, and hide it
          // once its trigger slot scrolls fully out of that container.
          collisionBoundary={boundary ?? undefined}
          collisionPadding={8}
          hideWhenDetached
          className={cn(
            'z-50 w-64 rounded-md border border-border bg-popover p-3 text-popover-foreground shadow-md',
            'animate-in fade-in-0 zoom-in-95 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2'
          )}
        >
          {/* Header */}
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="truncate text-sm font-semibold">
              {isOpen ? 'Open slot' : (patient?.name ?? patient?.initials ?? 'Booked')}
            </span>
            <span
              className={cn(
                'shrink-0 rounded px-1.5 py-0.5 text-xs font-medium uppercase tracking-wide',
                isOpen
                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400'
                  : (statusColor && cn(statusColor.bg, statusColor.text)) ||
                      'bg-muted text-muted-foreground'
              )}
            >
              {isOpen ? 'Available' : statusLabel}
            </span>
          </div>

          <div className="flex flex-col gap-1.5">
            {/* Patient — the name sits in the header above; phone + DOB complete
                the patient block, kept apart from the appointment details below. */}
            {!isOpen && patient?.phone && <Row icon={Phone}>{patient.phone}</Row>}
            {!isOpen && dobLabel && <Row icon={Cake}>{dobLabel}</Row>}

            {/* Appointment */}
            <div
              className={cn(
                'flex flex-col gap-1.5',
                !isOpen && 'mt-1 border-t border-border/60 pt-2'
              )}
            >
              {slot.appointmentTypeName && <Row icon={FileText}>{slot.appointmentTypeName}</Row>}
              <Row icon={Clock}>
                {timeRange(slot, displayTz)}
                <span className="text-muted-foreground"> &middot; {slot.durationMinutes} min</span>
              </Row>
              <Row icon={User}>{providerName}</Row>
              {locationName && <Row icon={MapPin}>{locationName}</Row>}
              {!isOpen && appt?.isTelehealth && <Row icon={Video}>Telehealth</Row>}
              {!isOpen && appt?.reasonForVisit && <Row icon={FileText}>{appt.reasonForVisit}</Row>}
              {!isOpen && appt?.notes && <Row icon={StickyNote}>{appt.notes}</Row>}
            </div>
          </div>

          <div className="mt-2 border-t border-border/60 pt-1.5 text-xs text-muted-foreground">
            {isOpen ? 'Click to book this slot' : 'Click for full details'}
          </div>
        </HoverCardPrimitive.Content>
      </HoverCardPrimitive.Portal>
    </HoverCardPrimitive.Root>
  );
}
